
import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, CheckCircleIcon, LockIcon, InfoIcon, AlertCircleIcon } from './icons';
import { useTranslation } from '../i18n/useTranslation';
import Spinner from './Spinner';
import { isEmailRegistered, getUserProfile } from '@shared/services/userService';
import { validateCouponCode } from '@shared/couponConfig';

interface AuthModalProps {
    isOpen: boolean;
    onClose: () => void;
}

type AuthStatus = 'idle' | 'processing' | 'success' | 'pending_confirmation' | 'pending_admin' | 'error';

const AuthModal: React.FC<AuthModalProps & { onEmailAuth?: (email: string, isSignup: boolean, password?: string) => Promise<void> }> = ({ isOpen, onClose, onEmailAuth }) => {
    const { t } = useTranslation();
    const [status, setStatus] = useState<AuthStatus>('idle');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [selectedProvider, setSelectedProvider] = useState<'google' | 'facebook' | 'drive' | 'photos' | 'email' | null>(null);
    const [isLoginMode, setIsLoginMode] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [couponCode, setCouponCode] = useState('');

    useEffect(() => {
        if (isOpen) {
            setStatus('idle');
            setErrorMessage(null);
            setSelectedProvider(null);
            setEmail('');
            setPassword('');
            setCouponCode('');
        }
    }, [isOpen]);


    const handleEmailSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email || !password || !onEmailAuth) return;

        setSelectedProvider('email');
        setErrorMessage(null);
        setStatus('processing');

        try {
            // Uniqueness validation on registration
            const emailRegistered = await isEmailRegistered(email);
            if (!isLoginMode && emailRegistered) {
                setStatus('error');
                setErrorMessage(t('auth_error_emailExists'));
                return;
            }

            // Simulate delay for realistic feel
            await new Promise(resolve => setTimeout(resolve, 800));

            // Perform auth through parent
            await onEmailAuth(email, !isLoginMode, password);

            // If signup, handle coupon code
            if (!isLoginMode && couponCode) {
                const membershipLevel = validateCouponCode(couponCode);
                if (membershipLevel) {
                    const userId = `${email.split('@')[0]}-user`;
                    const profile = await getUserProfile(userId);
                    const updatedProfile = { ...profile, membershipLevel };
                    // We need a way to save this. saveUserProfile is in userService.
                    const { saveUserProfile } = await import('@shared/services/userService');
                    await saveUserProfile(userId, updatedProfile);
                }
            }

            // After auth, check the resulting user profile for status
            // (Note: handleUserChange in NavBar already set the currentUserId)
            // We need a way to check THE user we just tried to auth
            const userId = `${email.split('@')[0]}-user`;
            const profile = await getUserProfile(userId);

            if (!isLoginMode) {
                // New signup
                setStatus('pending_confirmation');
            } else {
                // Login
                if (profile.accountStatus === 'registered') {
                    setStatus('pending_confirmation');
                } else if (profile.accountStatus === 'validated') {
                    setStatus('pending_admin');
                } else if (profile.accountStatus === 'inactive') {
                    setStatus('error');
                    setErrorMessage(t('auth_account_deactivated'));
                } else {
                    setStatus('success');
                    setTimeout(() => onClose(), 1500);
                }
            }
        } catch (error: any) {
            setStatus('error');
            setErrorMessage(error.message || t('agentWorkflow_error'));
        }
    };

    const getProviderName = (provider: string | null) => {
        switch (provider) {
            case 'google': return 'Google';
            case 'facebook': return 'Facebook';
            case 'drive': return 'Google Drive';
            case 'photos': return 'Google Photos';
            case 'email': return 'Email';
            default: return '';
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/60 backdrop-blur-md z-[100] flex items-center justify-center p-4"
                >
                    <motion.div
                        initial={{ scale: 0.95, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.95, y: 20 }}
                        className="relative bg-white dark:bg-gray-800 rounded-2xl w-full max-w-sm flex flex-col shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                    >
                        {(status === 'idle' || status === 'error') && (
                            <button onClick={onClose} className="absolute top-4 right-4 p-1 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 z-10" aria-label={t('common_close') || 'Close'}>
                                <XIcon className="w-5 h-5" />
                            </button>
                        )}

                        <div className="p-8 flex flex-col min-h-[400px]">
                            {(status === 'idle' || status === 'error') && (
                                <>
                                    <div className="text-center mb-6">
                                        <h2 className="text-2xl font-serif font-bold text-gray-900 dark:text-white mb-2">{t('authModal_title')}</h2>
                                        <p className="text-gray-500 dark:text-gray-400 text-sm">{t('authModal_subtitle')}</p>
                                    </div>

                                    {/* Tabs */}
                                    <div className="flex p-1 bg-gray-100 dark:bg-gray-700 rounded-xl mb-6">
                                        <button
                                            onClick={() => { setIsLoginMode(true); setErrorMessage(null); setStatus('idle'); }}
                                            className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${isLoginMode ? 'bg-white dark:bg-gray-600 shadow-sm text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}
                                        >
                                            {t('nav_user_action_login') || 'Sign In'}
                                        </button>
                                        <button
                                            onClick={() => { setIsLoginMode(false); setErrorMessage(null); setStatus('idle'); }}
                                            className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${!isLoginMode ? 'bg-white dark:bg-gray-600 shadow-sm text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}
                                        >
                                            {t('nav_signUp')}
                                        </button>
                                    </div>

                                    {status === 'error' && errorMessage && (
                                        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-xl flex items-center gap-3 text-red-600 dark:text-red-400 text-sm animate-shake">
                                            <AlertCircleIcon className="w-5 h-5 flex-shrink-0" />
                                            {errorMessage}
                                        </div>
                                    )}

                                    {/* Email Form */}
                                    <form onSubmit={handleEmailSubmit} className="space-y-4 mb-6">
                                        <input
                                            type="email"
                                            placeholder={t('nav_emailPlaceholder')}
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:bg-white outline-none focus:ring-2 focus:ring-indigo-500/20 dark:bg-gray-900/50 dark:border-gray-600 dark:text-white"
                                            required
                                        />
                                        <input
                                            type="password"
                                            placeholder={t('nav_passwordPlaceholder')}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:bg-white outline-none focus:ring-2 focus:ring-indigo-500/20 dark:bg-gray-900/50 dark:border-gray-600 dark:text-white"
                                            required
                                        />
                                        {!isLoginMode && (
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    placeholder={t('auth_coupon_placeholder') || 'Coupon Code (Optional)'}
                                                    value={couponCode}
                                                    onChange={(e) => setCouponCode(e.target.value)}
                                                    className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:bg-white outline-none focus:ring-2 focus:ring-indigo-500/20 dark:bg-gray-900/50 dark:border-gray-600 dark:text-white"
                                                />
                                            </div>
                                        )}
                                        <button
                                            type="submit"
                                            disabled={!onEmailAuth}
                                            className="w-full py-3 bg-gray-900 text-white rounded-xl text-sm font-semibold hover:bg-black transition-all dark:bg-indigo-600 dark:hover:bg-indigo-500 disabled:opacity-50"
                                        >
                                            {isLoginMode ? (t('nav_user_action_login') || 'Sign In') : t('nav_signUpButton')}
                                        </button>
                                    </form>

                                    <p className="mt-6 text-xs text-center text-gray-400 dark:text-gray-500 flex items-center justify-center gap-1">
                                        <LockIcon className="w-3 h-3" /> {t('authModal_secure_note')}
                                    </p>
                                </>
                            )}

                            {status === 'processing' && (
                                <div className="flex flex-col items-center justify-center h-full animate-fade-in my-auto">
                                    <Spinner className="w-12 h-12 text-indigo-600 dark:text-indigo-400 mb-4" />
                                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">{t('authModal_verifying')}</h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('authModal_connecting_provider', { provider: getProviderName(selectedProvider) })}</p>
                                </div>
                            )}

                            {status === 'pending_confirmation' && (
                                <div className="flex flex-col items-center justify-center h-full animate-fade-in my-auto text-center">
                                    <div className="bg-indigo-100 dark:bg-indigo-900/30 p-4 rounded-full mb-4">
                                        <InfoIcon className="w-12 h-12 text-indigo-600 dark:text-indigo-400" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('auth_pending_confirmation')}</h3>
                                    <button
                                        onClick={onClose}
                                        className="mt-6 text-sm font-semibold text-indigo-600 hover:text-indigo-500"
                                    >
                                        {t('common_close')}
                                    </button>
                                </div>
                            )}

                            {status === 'pending_admin' && (
                                <div className="flex flex-col items-center justify-center h-full animate-fade-in my-auto text-center">
                                    <div className="bg-amber-100 dark:bg-amber-900/30 p-4 rounded-full mb-4">
                                        <CheckCircleIcon className="w-12 h-12 text-amber-600 dark:text-amber-400" />
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{t('auth_pending_admin')}</h3>
                                    <button
                                        onClick={onClose}
                                        className="mt-6 text-sm font-semibold text-indigo-600 hover:text-indigo-500"
                                    >
                                        {t('common_close')}
                                    </button>
                                </div>
                            )}

                            {status === 'success' && (
                                <div className="flex flex-col items-center justify-center h-full animate-fade-in my-auto">
                                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="bg-green-100 dark:bg-green-900/30 p-3 rounded-full mb-4">
                                        <CheckCircleIcon className="w-12 h-12 text-green-600 dark:text-green-400" />
                                    </motion.div>
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">{t('authModal_success')}</h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('authModal_redirecting')}</p>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default AuthModal;
